import React, { useState } from 'react';
import { FileText, Plus, Save, UserPlus, X } from 'lucide-react';
import { WORK_TYPES, OFFICES, CONTACT_TYPES, BANK_SUGGESTIONS, CONTACT_TYPE_TO_CATEGORY } from './helpers';

const MAX_ROWS = 6;

const wtChipClass = (t) =>
  t === 'Stage Bill' ? 'wt-stagebill'
  : t === 'Stability Certificate' ? 'wt-stability'
  : t === 'Estimation & Valuation' ? 'wt-estimation'
  : 'wt-valuation';

const emptyRow = () => ({
  _key: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  client_name: '', mobile: '', contact_type: 'Direct Client',
  location: '', notes: '', saved: false,
});

const rowFromWork = (w) => ({
  _key: 'edit-row',
  client_name: w.client_name === 'Name not added' ? '' : (w.client_name || ''),
  mobile: w.mobile || '',
  contact_type: w.contact_type || 'Direct Client',
  location: w.location || '',
  notes: w.notes || '',
  saved: false,
});

// ===========================================================================
// Sub-component: per-report row
// ===========================================================================
function ReportRow({ idx, row, setRow, onRemove, canRemove, onSaveToContacts, alreadyInContacts, saveDisabled }) {
  const saved = row.saved || alreadyInContacts;
  return (
    <div className="report-row" data-testid={`report-row-${idx}`}>
      <div className="report-row-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span><FileText className="icon" /> Report {idx + 1}</span>
        {canRemove && (
          <button type="button" onClick={onRemove}
            style={{ background: 'none', border: 'none', color: '#be123c', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}
            data-testid={`remove-row-${idx}`}>
            <X className="icon" /> Remove
          </button>
        )}
      </div>
      <div className="field">
        <label className="field-label">Client Name</label>
        <input className="field-input" value={row.client_name}
          onChange={(e) => setRow('client_name', e.target.value)}
          data-testid={`field-name-${idx}`} />
      </div>
      <div className="field-row">
        <div className="field">
          <label className="field-label">Mobile</label>
          <input className="field-input" type="tel" inputMode="numeric" maxLength={10}
            value={row.mobile}
            onChange={(e) => setRow('mobile', e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
            data-testid={`field-mobile-${idx}`} />
        </div>
        <div className="field">
          <label className="field-label">Contact Type</label>
          <select className="field-input" value={row.contact_type}
            onChange={(e) => setRow('contact_type', e.target.value)}
            data-testid={`field-contact-${idx}`}>
            {CONTACT_TYPES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="field">
        <label className="field-label">Location</label>
        <input className="field-input" value={row.location}
          onChange={(e) => setRow('location', e.target.value)}
          data-testid={`field-location-${idx}`} />
      </div>
      <div className="field">
        <label className="field-label">Notes (optional)</label>
        <textarea className="field-input" value={row.notes}
          onChange={(e) => setRow('notes', e.target.value)}
          rows={2}
          placeholder="Anything to remember about this report…"
          data-testid={`field-notes-${idx}`} />
      </div>
      {onSaveToContacts && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <button type="button"
            className={`save-to-contacts-btn${saved ? ' saved' : ''}`}
            onClick={onSaveToContacts} disabled={saveDisabled}
            data-testid={`save-to-contacts-${idx}`}>
            <Save className="icon" /> {saved ? 'Saved to Contacts' : 'Save to Contacts'}
          </button>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Main modal
// ===========================================================================
export default function NewWorkModal({ work, staff, contacts = [], onSaveContact, onOpenNewContact, onClose, onSubmit }) {
  const isEdit = !!work;
  const staffFor = (off) => staff.filter((s) => s.office === off && s.role === 'staff');

  const initialBank = isEdit ? (work.bank === 'Bank not added' ? '' : (work.bank || '')) : '';
  const initialBankIsPreset = BANK_SUGGESTIONS.includes(initialBank);

  const [workType, setWorkType] = useState(isEdit ? (work.work_type || 'Valuation Report') : 'Valuation Report');
  const [bankPick, setBankPick] = useState(initialBank ? (initialBankIsPreset ? initialBank : '__other__') : '');
  const [bankOther, setBankOther] = useState(initialBank && !initialBankIsPreset ? initialBank : '');
  const [branch, setBranch] = useState(isEdit ? (work.branch || '') : '');
  const [office, setOfficeRaw] = useState(isEdit ? (work.office || 'Karaikudi') : 'Karaikudi');
  const [staffId, setStaffId] = useState(() => {
    const off = isEdit ? (work.office || 'Karaikudi') : 'Karaikudi';
    return isEdit ? (work.staff_id || staffFor(off)[0]?.user_id || '') : (staffFor(off)[0]?.user_id || '');
  });
  const [rows, setRows] = useState(isEdit ? [rowFromWork(work)] : [emptyRow()]);
  const [saving, setSaving] = useState(false);

  const isStability = workType === 'Stability Certificate';
  const officeStaff = staffFor(office);
  const bankFinal = bankPick === '__other__' ? bankOther.trim() : bankPick;

  const setOffice = (off) => {
    setOfficeRaw(off);
    const list = staffFor(off);
    if (!list.some((s) => s.user_id === staffId)) setStaffId(list[0]?.user_id || '');
  };
  const updateRow = (i, k, v) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((p) => (p.length >= MAX_ROWS ? p : [...p, emptyRow()]));
  const removeRow = (i) => setRows((p) => (p.length === 1 ? p : p.filter((_, idx) => idx !== i)));

  const isInContacts = (row) => {
    const nm = (row.client_name || '').trim().toLowerCase();
    const mb = (row.mobile || '').trim();
    if (!nm) return false;
    return contacts.some((c) => {
      if (mb && c.mobile && c.mobile === mb) return true;
      return (c.name || '').trim().toLowerCase() === nm;
    });
  };

  const saveRowToContacts = async (i) => {
    const row = rows[i];
    const category = CONTACT_TYPE_TO_CATEGORY[row.contact_type];
    const ok = await onSaveContact({
      name: row.client_name, mobile: row.mobile,
      category, location: row.location,
    });
    if (ok) updateRow(i, 'saved', true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const shared = {
        work_type: workType,
        bank: isStability ? '' : bankFinal,
        branch: isStability ? '' : branch.trim(),
        office,
        staff_id: staffId || null,
      };
      if (isEdit) {
        const r = rows[0];
        await onSubmit({ mode: 'edit', id: work.id, data: {
          ...shared,
          client_name: r.client_name.trim() || 'Name not added',
          mobile: r.mobile.trim(),
          contact_type: r.contact_type,
          location: r.location.trim(),
          notes: r.notes.trim(),
        } });
      } else {
        const list = rows.map((r) => ({
          ...shared,
          client_name: r.client_name.trim() || 'Name not added',
          mobile: r.mobile.trim(),
          contact_type: r.contact_type,
          location: r.location.trim(),
          notes: r.notes.trim(),
        }));
        await onSubmit({ mode: 'create', list });
      }
    } finally { setSaving(false); }
  };

  const count = rows.length;
  const saveLabel = isEdit ? 'Update Work' : (count === 1 ? 'Save Work' : `Save ${count} Works`);

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" data-testid="new-work-modal">
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Work' : 'New Work'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="field">
          <label className="field-label">Work Type *</label>
          <div className="worktype-chips" data-testid="field-worktype">
            {WORK_TYPES.map((t) => (
              <button key={t} type="button"
                className={`worktype-chip ${wtChipClass(t)}${workType === t ? ' selected' : ''}`}
                onClick={() => setWorkType(t)}
                data-testid={`worktype-${t.replace(/\s+/g, '-').toLowerCase()}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {!isStability && (
          <>
            <div className="field">
              <label className="field-label">Bank Name {!isEdit && count > 1 && <span style={{ color: '#a8a29e', fontWeight: 500, fontStyle: 'italic' }}>(applies to all {count} reports)</span>}</label>
              <select className="field-input" value={bankPick}
                onChange={(e) => setBankPick(e.target.value)}
                data-testid="field-bank-select">
                <option value="">— Select bank —</option>
                {BANK_SUGGESTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                <option value="__other__">Other (type below)</option>
              </select>
              {bankPick === '__other__' && (
                <input className="field-input" style={{ marginTop: 8 }}
                  value={bankOther} onChange={(e) => setBankOther(e.target.value)}
                  placeholder="Type bank name…" autoFocus data-testid="field-bank-other" />
              )}
            </div>
            <div className="field">
              <label className="field-label">Branch</label>
              <input className="field-input" value={branch}
                onChange={(e) => setBranch(e.target.value)} data-testid="field-branch" />
            </div>
          </>
        )}

        {rows.map((r, i) => (
          <ReportRow key={r._key} idx={i} row={r}
            setRow={(k, v) => updateRow(i, k, v)}
            onRemove={() => removeRow(i)}
            canRemove={!isEdit && rows.length > 1}
            onSaveToContacts={onSaveContact ? () => saveRowToContacts(i) : null}
            alreadyInContacts={isInContacts(r)}
            saveDisabled={!r.client_name.trim() || r.saved || isInContacts(r)} />
        ))}

        {!isEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '8px 0 4px' }}>
            <button type="button" className="add-another-row-btn"
              onClick={addRow} disabled={rows.length >= MAX_ROWS}
              data-testid="add-another-report-btn">
              <Plus className="icon" /> + Add Another Report {rows.length >= MAX_ROWS && `(max ${MAX_ROWS})`}
            </button>
            {onOpenNewContact && (
              <button type="button" className="add-another-contact-btn"
                onClick={onOpenNewContact} data-testid="add-another-contact-btn">
                <UserPlus className="icon" /> + Add Another Contact to CRM
              </button>
            )}
          </div>
        )}

        <div className="field-row">
          <div className="field">
            <label className="field-label">Office</label>
            <select className="field-input" value={office} onChange={(e) => setOffice(e.target.value)} data-testid="field-office">
              {OFFICES.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Assign Staff</label>
            <select className="field-input" value={staffId || ''}
              onChange={(e) => setStaffId(e.target.value)} data-testid="field-staff">
              {officeStaff.length === 0 && <option value="">No staff</option>}
              {officeStaff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={saving} data-testid="save-work-btn">
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
